import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  calcTotalRisk,
  calcAccountRiskPercent,
  filterOpenTradesByWidgetSettings,
  getHighestRiskPositions,
  formatFilterCriteria,
  type WidgetFilterSettings,
} from '../lib/tradeCalculations'
import { convertToDisplayCurrency } from '../hooks/useDashboardCurrency'
import { navigateToTradeDetail } from '../utils/tradeNavigation'
import type { Trade } from '../types/trade'
import DashboardWidgetTitle from './DashboardWidgetTitle'

interface WidgetAccount {
  id: string
  name: string
  platform: string
  currency: string
  initial_balance?: number | null
}

interface DashboardWidgetTotalRiskProps {
  trades: Trade[]
  selectedCurrency: string
  latestRates: Map<string, number>
  filterSettings?: WidgetFilterSettings
  accounts?: WidgetAccount[]
}

const cardClass =
  'h-full w-full dashboard-card p-3 md:p-4 flex flex-col justify-center'

export default function DashboardWidgetTotalRisk({
  trades,
  selectedCurrency,
  latestRates,
  filterSettings,
  accounts = [],
}: DashboardWidgetTotalRiskProps) {
  const { t } = useTranslation(['trading'])
  const navigate = useNavigate()
  const [showHighRiskModal, setShowHighRiskModal] = useState(false)

  const openTrades = useMemo(() => trades.filter((trade) => !trade.exit_date), [trades])

  const filteredOpenTrades = useMemo(
    () => filterOpenTradesByWidgetSettings(openTrades, filterSettings),
    [openTrades, filterSettings],
  )

  const criteriaStr = useMemo(
    () => (filterSettings ? formatFilterCriteria(filterSettings, [], accounts) : ''),
    [filterSettings, accounts]
  )

  const totalRiskByCurrency = useMemo(() => calcTotalRisk(filteredOpenTrades), [filteredOpenTrades])
  const totalRiskInDisplayCurrency = useMemo(() => {
    let total = 0
    for (const [currency, amount] of Object.entries(totalRiskByCurrency)) {
      const curr = currency || 'USD'
      total += convertToDisplayCurrency(amount, curr, selectedCurrency, latestRates)
    }
    return total
  }, [totalRiskByCurrency, selectedCurrency, latestRates])
  const highestRisk = useMemo(() => getHighestRiskPositions(filteredOpenTrades, 5), [filteredOpenTrades])

  const accountRiskPercent = useMemo(
    () =>
      calcAccountRiskPercent(trades, accounts, selectedCurrency, latestRates, {
        accountId: filterSettings?.accountId,
        openTrades: filteredOpenTrades,
      }),
    [filterSettings?.accountId, accounts, trades, filteredOpenTrades, selectedCurrency, latestRates],
  )

  const formatPrice = (price: number | null | undefined, currency?: string | null) => {
    if (price === null || price === undefined) return '-'
    const formatted = price.toFixed(2).replace('.', ',')
    return currency ? `${formatted} ${currency}` : formatted
  }

  // True when accounts exist but none has initial_balance set (so % can't be computed)
  const accountSelectedNoBalance = useMemo(() => {
    if (accounts.length === 0) return false
    const accId = filterSettings?.accountId || ''
    const targetAccounts = accId ? accounts.filter((a) => a.id === accId) : accounts
    if (targetAccounts.length === 0) return false
    return targetAccounts.every(
      (a) => a.initial_balance == null || isNaN(Number(a.initial_balance))
    )
  }, [filterSettings?.accountId, accounts])

  const riskPctColor =
    accountRiskPercent == null
      ? ''
      : accountRiskPercent < 1
        ? 'text-green-600'
        : accountRiskPercent < 2
          ? 'text-yellow-600'
          : 'text-red-600'

  const handleTradeClick = (trade: Parameters<typeof navigateToTradeDetail>[0]) => {
    navigateToTradeDetail(trade, navigate, null)
  }

  return (
    <>
      <div className={cardClass}>
        {/* Mobile: başlık, değer, filtre/link yan yana */}
        <div className="flex md:hidden flex-row items-center justify-between gap-2 py-0.5">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider truncate shrink-0">
            <DashboardWidgetTitle widgetId="totalRiskExposure" />
          </p>
          <div className="flex items-baseline gap-1.5 shrink-0">
            {Object.keys(totalRiskByCurrency).length > 0 ? (
              <span className="text-base font-bold text-gray-900">
                {totalRiskInDisplayCurrency.toFixed(2).replace('.', ',')}{' '}
                <span className="text-[10px] font-normal text-gray-500">{selectedCurrency}</span>
              </span>
            ) : (
              <p className="text-base font-bold text-gray-900">–</p>
            )}
            {accountRiskPercent != null && (
              <span className={`text-[10px] font-semibold ${riskPctColor}`}>
                {accountRiskPercent.toFixed(2).replace('.', ',')}%
              </span>
            )}
            {accountSelectedNoBalance && (
              <span
                className="text-[10px] text-gray-400"
                title="Set initial balance in Accounts page to see risk %"
              >
                %?
              </span>
            )}
          </div>
          {highestRisk.length > 0 ? (
            <button
              type="button"
              onClick={() => setShowHighRiskModal(true)}
              className="flex-shrink-0 text-[10px] font-medium text-orange-600 hover:text-orange-700 hover:underline"
            >
              {t('trading:dashboard.highestRiskPositions')}
            </button>
          ) : (
            <span className="w-0" />
          )}
        </div>
        {/* Desktop: üstte başlık, altta değer + link */}
        <div className="hidden md:block">
          <div className="flex items-baseline justify-between gap-1 mb-1">
            <p className="truncate text-xs font-medium text-gray-500 uppercase tracking-wider">
              <DashboardWidgetTitle widgetId="totalRiskExposure" />
            </p>
            {criteriaStr && (
              <span className="shrink-0 truncate text-[10px] text-gray-400" title={criteriaStr}>
                {criteriaStr}
              </span>
            )}
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <div className="flex items-baseline gap-2 min-w-0">
              {Object.keys(totalRiskByCurrency).length > 0 ? (
                <span className="text-xl md:text-2xl font-bold text-gray-900">
                  {totalRiskInDisplayCurrency.toFixed(2).replace('.', ',')}{' '}
                  <span className="text-xs font-normal text-gray-500">{selectedCurrency}</span>
                </span>
              ) : (
                <p className="text-xl md:text-2xl font-bold text-gray-900">–</p>
              )}
              {accountRiskPercent != null && (
                <span className={`shrink-0 text-sm font-semibold ${riskPctColor}`}>
                  {accountRiskPercent.toFixed(2).replace('.', ',')}%
                </span>
              )}
              {accountSelectedNoBalance && (
                <span
                  className="shrink-0 text-xs text-gray-400"
                  title="Set initial balance in Accounts page to see risk %"
                >
                  %?
                </span>
              )}
            </div>
            {highestRisk.length > 0 && (
              <button
                type="button"
                onClick={() => setShowHighRiskModal(true)}
                className="flex-shrink-0 text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline"
              >
                {t('trading:dashboard.highestRiskPositions')}
              </button>
            )}
          </div>
        </div>
      </div>

      {showHighRiskModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowHighRiskModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t('trading:dashboard.highestRiskPositions')}
        >
          <div
            className="bg-app-box border border-gray-200 rounded-2xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-base font-semibold text-gray-900">
                {t('trading:dashboard.highestRiskPositions')}
              </h3>
              <button
                type="button"
                onClick={() => setShowHighRiskModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-600"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2 flex-1 min-h-0">
              {highestRisk.map((trade) => (
                <button
                  key={trade.id}
                  type="button"
                  onClick={() => {
                    setShowHighRiskModal(false)
                    handleTradeClick(trade)
                  }}
                  className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-medium text-gray-900 truncate">{trade.symbol}</span>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                        trade.long_short === 'long' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {trade.long_short.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-700">{trade.riskPercent.toFixed(1)}%</span>
                    {trade.riskAmount !== null && (
                      <span className="block text-xs text-gray-500">
                        {formatPrice(trade.riskAmount, trade.currency)}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
